// src/components/UploadModal.jsx
import React, { useState } from "react";
import { db, auth, firebaseConfig } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { optimizeImageForUpload, createThumbnailForUpload } from "../utils/imageUploadUtils";

const ENV = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host.includes('staging')) {
      return 'staging';
    }
  }
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV) {
    return process.env.REACT_APP_ENV;
  }
  return 'prod';
})();

const bookTags = [
  "Around the world with 100 artists",
  "What's your story",
  "Complete the story",
  "Impressionism",
  "Pointillism",
  "Misc",
];

export default function UploadModal({ onClose, onUploaded }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tag, setTag] = useState(bookTags[0]);
  const [file, setFile] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);

  const storage = getStorage();

  const handleUpload = async () => {
    const user = auth.currentUser;
    if (!user || !file || !title || !description) {
      alert("All fields are required.");
      return;
    }

    try {
      setLoading(true);
      console.log('[UploadModal] Starting upload', {
        ENV,
        projectId: firebaseConfig?.projectId,
        storageBucket: firebaseConfig?.storageBucket,
        userId: user.uid,
      });
      const path = `users/${user.uid}/artworks/${uuidv4()}`;
      const fileRef = ref(storage, path);
      const optimizedFile = await optimizeImageForUpload(file);
      const thumbnailFile = await createThumbnailForUpload(optimizedFile || file);
      await uploadBytes(fileRef, optimizedFile || file);
      const url = await getDownloadURL(fileRef);
      let thumbnailUrl = url;
      if (thumbnailFile) {
        const thumbPath = `users/${user.uid}/artworks/thumbnails/${uuidv4()}_thumb.jpg`;
        const thumbRef = ref(storage, thumbPath);
        await uploadBytes(thumbRef, thumbnailFile);
        thumbnailUrl = await getDownloadURL(thumbRef);
      }

      const docRef = await addDoc(collection(db, "artworks"), {
        uid: user.uid,
        title,
        description,
        tag,
        imageUrl: url,
        thumbnailUrl,
        isPublic,
        environment: ENV,
        projectId: firebaseConfig?.projectId || null,
        createdAt: serverTimestamp(),
      });
      console.log('[UploadModal] Artwork document created', {
        docId: docRef.id,
        environment: ENV,
        projectId: firebaseConfig?.projectId,
        imageUrl: url,
        storagePath: path,
      });

      if (onUploaded) {
        onUploaded({
          docId: docRef.id,
          title,
          description,
          tag,
          imageUrl: url,
          thumbnailUrl,
        });
      }

      onClose();
    } catch (err) {
      console.error("Upload failed", err);
      alert("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white/10 backdrop-blur-md flex justify-center items-center p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg relative">
        <button onClick={onClose} className="absolute top-2 right-3 text-gray-500 hover:text-red-600 text-xl">&times;</button>
        <h3 className="text-lg font-bold text-amber-700 mb-4">Upload Your Artwork</h3>
        <input
          type="text"
          placeholder="Title"
          className="w-full p-2 border mb-2 rounded"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          placeholder="Description"
          className="w-full p-2 border mb-2 rounded"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <select
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          className="w-full p-2 border mb-2 rounded"
        >
          {bookTags.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          accept="image/*"
          className="w-full mb-2"
        />
        <label className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
          />
          Make Public
        </label>
        <button
          onClick={handleUpload}
          disabled={loading}
          className="w-full bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition"
        >
          {loading ? "Uploading..." : "Upload"}
        </button>
      </div>
    </div>
  );
}
